/**
 * Store Pinia pour le cache des noms d'utilisateurs
 * Gère un cache local des noms d'utilisateurs pour éviter les appels API répétés
 */

import { apiClient } from '@/services/api';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

interface UserNamesCache {
  [uuid: string]: {
    name: string;
    timestamp: number;
  };
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes en millisecondes

export const useUserNamesCacheStore = defineStore('userNamesCache', () => {
  // État
  const cache = ref<UserNamesCache>({});
  const pendingRequests = ref<Set<string>>(new Set());

  // Getters
  const getUserName = computed(() => {
    return (uuid: string): string | null => {
      const cached = cache.value[uuid];
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.name;
      }
      return null;
    };
  });

  const isCached = computed(() => {
    return (uuid: string): boolean => {
      const cached = cache.value[uuid];
      return !!(cached && Date.now() - cached.timestamp < CACHE_DURATION);
    };
  });

  // Actions
  const fetchUserNames = async (uuids: string[]): Promise<Record<string, string>> => {
    if (!uuids.length) return {};

    // Filtrer les UUIDs qui ne sont pas en cache ou expirés
    const uncachedUuids = uuids.filter(uuid => !isCached.value(uuid));
    
    if (!uncachedUuids.length) {
      // Tous les noms sont en cache, les retourner
      const result: Record<string, string> = {};
      uuids.forEach(uuid => {
        const cached = cache.value[uuid];
        if (cached) {
          result[uuid] = cached.name;
        }
      });
      return result;
    }

    // Vérifier s'il y a déjà une requête en cours pour ces UUIDs
    const requestKey = uncachedUuids.sort().join(',');
    if (pendingRequests.value.has(requestKey)) {
      // Attendre que la requête en cours se termine
      return new Promise((resolve) => {
        const checkPending = () => {
          if (!pendingRequests.value.has(requestKey)) {
            const result: Record<string, string> = {};
            uuids.forEach(uuid => {
              const cached = cache.value[uuid];
              if (cached) {
                result[uuid] = cached.name;
              }
            });
            resolve(result);
          } else {
            setTimeout(checkPending, 100);
          }
        };
        checkPending();
      });
    }

    // Marquer la requête comme en cours
    pendingRequests.value.add(requestKey);

    try {
      // Appel API pour récupérer les noms
      const response = await apiClient.post<Record<string, string>>('/users/names', uncachedUuids);
      const userNames = response.data;

      // Mettre à jour le cache
      const timestamp = Date.now();
      Object.entries(userNames).forEach(([uuid, name]) => {
        cache.value[uuid] = { name: name as string, timestamp };
      });

      // Retourner tous les noms demandés (cache + nouveaux)
      const result: Record<string, string> = {};
      uuids.forEach(uuid => {
        const cached = cache.value[uuid];
        if (cached) {
          result[uuid] = cached.name;
        }
      });

      return result;
    } catch (error) {
      console.error('Erreur lors de la récupération des noms d\'utilisateurs:', error);
      return {};
    } finally {
      // Retirer la requête de la liste des requêtes en cours
      pendingRequests.value.delete(requestKey);
    }
  };

  const getUserNameAsync = async (uuid: string): Promise<string | null> => {
    // Vérifier d'abord le cache
    const cached = cache.value[uuid];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.name;
    }

    // Si pas en cache, faire un appel API
    const result = await fetchUserNames([uuid]);
    return result[uuid] || null;
  };

  const clearCache = (): void => {
    cache.value = {};
  };

  const removeFromCache = (uuid: string): void => {
    delete cache.value[uuid];
  };

  return {
    // Getters
    getUserName,
    isCached,
    
    // Actions
    fetchUserNames,
    getUserNameAsync,
    clearCache,
    removeFromCache,
  };
});
